import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from './index';
import { InvitationLinkSection } from './InvitationLinkSection';
import { adminApi } from '../services/api';
import type { InvitationDetails } from '../types';

export type ProviderType = 'inspector' | 'lawyer';

/** Error text for creating a Besiktningsman or Jurist, by the API's error code. */
export const providerCreateErrorKey = (type: ProviderType, code: string | undefined) => {
  switch (code) {
    case 'ORG_NUMBER_ALREADY_EXISTS': return 'orgNumber.taken';
    case 'EMAIL_ALREADY_EXISTS': return 'providerInvite.errors.otherRole';
    case 'PROVIDER_REGISTRATION_INCOMPLETE': return 'providerInvite.errors.registrationIncomplete';
    case 'INVITATION_ALREADY_SENT': return 'providerInvite.errors.alreadyInvited';
    default: return type === 'lawyer' ? 'lawyers.form.saveError' : 'inspectors.form.saveError';
  }
};

/**
 * After admin added a Besiktningsman or Jurist without an account (ClickUp
 * 869fjt7ma): the invitation was sent, with the link to pass on and a way to
 * send it again. Shared by both create pages.
 */
export function ProviderInvitationResult({ type, invitation: initial }: { type: ProviderType; invitation: InvitationDetails }) {
  const { t, i18n } = useTranslation();
  const [invitation, setInvitation] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const listPath = type === 'lawyer' ? '/admin/lawyers' : '/admin/inspectors';

  const resend = async () => {
    setBusy(true); setError('');
    try { setInvitation(await adminApi.resendProviderInvitation(invitation.id)); }
    catch { setError(t('providerInvite.resendError')); }
    finally { setBusy(false); }
  };

  return <Card>
    <div className="card-body" data-testid="provider-invitation-result">
      <h2 className="h5">{t(`providerInvite.invited.${type}`, { name: invitation.companyName || invitation.contactEmail })}</h2>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className={`alert ${invitation.emailStatus === 'sent' ? 'alert-success' : 'alert-warning'}`} role="status">
        {t(invitation.emailStatus === 'sent' ? 'providerInvite.sent' : 'providerInvite.notSent', { email: invitation.contactEmail })}
      </div>
      <p>{t('providerInvite.next')}</p>
      <InvitationLinkSection invitation={invitation} personalText={t(`providerInvite.personal.${type}`)}
        testId="provider-invitation-link" deliveryTestId="provider-invitation-delivery" />
      {invitation.expiresAt && <p>{t('companySetup.expires', { date: new Intl.DateTimeFormat(i18n.language).format(new Date(invitation.expiresAt)) })}</p>}
      <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
        <Link className="btn btn-primary provider-invitation-action" to={listPath}>{t(`providerInvite.backToList.${type}`)}</Link>
        <button className="btn btn-outline-primary provider-invitation-action" type="button" disabled={busy} onClick={() => void resend()}>
          {busy ? t('providerInvite.resending') : t('providerInvite.resend')}
        </button>
      </div>
    </div>
  </Card>;
}
